import { BehaviorSubject } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import CookieUtils from '../../../utils/CookieUtils';
import { ERRORS } from '../Errors';
import { LiveEvents } from '../model/LiveEvents';

export class WebSocketApi {
    private liveEvents: ReplaySubject<LiveEvents.Event> | undefined;
    private ws: WebSocket;
    private heartbeat: any;
    private status: BehaviorSubject<boolean | Error | undefined>;

    constructor(fixtureId: number, legacy: boolean) {
        this.status = new BehaviorSubject<boolean | Error | undefined>(undefined);
        this.liveEvents = new ReplaySubject<LiveEvents.Event>(Infinity);
        this.init(fixtureId, legacy);
    }

    /** Creates a websocket, starts a heartbeat and begins to collect LiveEvents */ 
    private async init(id: number, legacy: boolean): Promise<void> {

        let liveApiUrl: string;
        if(legacy) {
            liveApiUrl = process.env.NODE_ENV === "production" ? "wss://api.gamescorekeeper.com/v1/liveapi" : "wss://dev.gamescorekeeper.com/liveapi";
        } else {
            liveApiUrl =  process.env.NODE_ENV === "production" ? "wss://api.gamescorekeeper.com/v2/live" : "wss://dev.gamescorekeeper.com/v2/live";
        }
        this.ws = new WebSocket(`${liveApiUrl}/${id}`);
        // const liveApiUrl: string = process.env.NODE_ENV === "production" ? `wss://api.gamescorekeeper.com/${version}/liveapi` : `wss://dev.gamescorekeeper.com/${version}/liveapi`;
        this.ws.onmessage = (msgEvt: MessageEvent) => this.handleEvent(JSON.parse(msgEvt.data));
        this.ws.onclose = (closeEvt: CloseEvent) => {
            clearInterval(this.heartbeat);
            this.liveEvents!.complete();
        };
        this.ws.onerror = (err) => {
            this.status.next(new Error("Websocket error"));
            this.liveEvents!.error(err);
        };
        this.heartbeat = setInterval(() => this.ws.send("ping"), 30000);
        // TODO ERR_NAME_NOT_RESOLVED
    }

    /** Returns a promise of the live events once logged in*/
    public async getLiveData(): Promise<ReplaySubject<LiveEvents.Event>> {
        const status: boolean | Error | undefined = await this.status.pipe(filter(state => state !== undefined), first()).toPromise();
        if (status instanceof Error) {
            throw status;
        }
        return this.liveEvents!;
        // TODO timeout when awaiting auth
    }

    private handleEvent(event: LiveEvents.Event) {
        switch (JSON.stringify(event)) {
            case JSON.stringify(AuthRequestPayload): case JSON.stringify(AuthRequestPayloadV2): {
                this.ws.send(JSON.stringify({ token: CookieUtils.getCookie("GameScorekeeper") }));
                break;
            }
            case JSON.stringify(AuthErrorPayload): {
                this.ws.close();
                clearInterval(this.heartbeat);
                this.status.next(new Error(ERRORS.WEBSOCKET_401));
                break;
            }
            case JSON.stringify(PongPayload): {
                break;
            }
            case JSON.stringify(EndedPayload): {
                this.ws.close();
                clearInterval(this.heartbeat);
                this.status.next(new Error(ERRORS.WEBSOCKET_ENDED));
                break;
            }
            default: {
                if (!this.status.getValue()) {
                    this.status.next(true);
                }
                if (event.type === "fixture_ended") {
                    this.ws.close();
                    clearInterval(this.heartbeat);
                    break;
                }
                this.liveEvents!.next(event);
                break;
            }
        }
    }

    public getSocket(): WebSocket | undefined {
        return this.ws;
    }

    public closeSocket(): void {
        this.ws.close();
    }
}

interface Event {
    payload?: string;
    sortIndex?: number;
    type: string;
}

const AuthRequestPayload: Event = {
    payload: "",
    sortIndex: 0,
    type: "auth"
}

// TODO why isn't v2 doing this like v1 is?
const AuthRequestPayloadV2: any = {
    type: "auth"
}

const AuthErrorPayload: Event = {
    payload: "Authentication failed",
    sortIndex: 0,
    type: "auth"
}

const PongPayload: Event = {
    payload: "",
    sortIndex: 0,
    type: "pong"
}

const EndedPayload: Event = {
    type: "ended"
}